import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/constants";
import api, { apiErr } from "../lib/api";
import {
  LayoutDashboard, ClipboardList, GanttChartSquare, ShieldCheck, UserCircle2,
  Settings, Search, Bell, LogOut, Eye, Activity, FileText, Users, ScrollText, Home, ShieldAlert, BookOpen, Boxes,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Home", icon: Home, roles: ["it_admin", "pmo", "executive", "it_executive", "engineer"] },
  { to: "/dashboard", label: "Executive Dashboard", icon: LayoutDashboard, roles: ["it_admin", "pmo", "executive", "it_executive"] },
  { to: "/assessment", label: "Assessment & Mitigation", icon: ClipboardList, roles: ["it_admin", "pmo", "executive", "it_executive"] },
  { to: "/inventory", label: "Asset & Contract Inventory", icon: Boxes, roles: ["it_admin", "pmo", "executive", "it_executive", "engineer"] },
  { to: "/project-plan", label: "Project Plan", icon: GanttChartSquare, roles: ["it_admin", "pmo", "executive", "it_executive"] },
  { to: "/pmo", label: "PMO Control Center", icon: ShieldCheck, roles: ["it_admin", "pmo"] },
  { to: "/my-responsibility", label: "My Responsibility", icon: UserCircle2, roles: ["it_admin", "pmo", "executive", "it_executive", "engineer"] },
  { to: "/reports", label: "Reports", icon: FileText, roles: ["it_admin", "pmo", "executive"] },
  { to: "/compliance", label: "Compliance", icon: ShieldAlert, roles: ["it_admin", "pmo", "executive"] },
  { to: "/admin/users", label: "Users", icon: Users, roles: ["it_admin"] },
  { to: "/admin/config", label: "Configuration", icon: Settings, roles: ["it_admin"] },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, roles: ["it_admin", "pmo"] },
];

export default function Layout({ children }) {
  const { user, logout, effectiveRole, viewAs, setViewAs } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const searchRef = useRef();

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 20000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/search?q=${encodeURIComponent(q)}`).then((r) => setResults(r.data.results)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const unread = notifs.filter((n) => !n.read).length;
  const items = NAV.filter((n) => n.roles.includes(effectiveRole));

  const markAllRead = async () => { await api.post("/notifications/read-all"); loadNotifs(); };

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#DCE2EA] shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
            <img src="https://customer-assets-jai6qajn.emergentagent.net/job_transform-dashboard-2/artifacts/5gfc3ket_download.jpg" alt="Carle Health" className="h-8 w-auto" />
            <div className="hidden md:block leading-tight border-l border-[#DCE2EA] pl-2.5">
              <div className="text-[11px] text-[#5A687C] font-semibold">IT Digital Transformation</div>
            </div>
          </Link>

          <div className="relative flex-1 max-w-md hidden md:block" ref={searchRef}>
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#98A2AF]" />
            <input
              data-testid="global-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search workstreams, findings, tasks…"
              className="w-full pl-10 pr-4 py-2 rounded-full bg-[#F4F6FA] border border-[#DCE2EA] text-sm focus:bg-white transition-colors"
            />
            {results.length > 0 && (
              <div className="absolute mt-2 w-full bg-white rounded-2xl border border-[#DCE2EA] shadow-lg overflow-hidden z-50" data-testid="search-results">
                {results.map((r) => (
                  <button key={r.type + r.id} onClick={() => { nav(r.link); setQ(""); setResults([]); }}
                    className="w-full text-left px-4 py-2.5 hover:bg-[#F4F6FA] flex items-center gap-3 border-b border-[#DCE2EA]/50 last:border-0">
                    <span className="text-[10px] uppercase font-bold text-[#BA0C2F] w-20 shrink-0">{r.type}</span>
                    <span className="text-sm text-[#1F2733] truncate">{r.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {user?.role === "it_admin" && (
              <div className="hidden lg:flex items-center gap-1.5 bg-[#FBEAED] border border-[#F1B9C3] rounded-full pl-3 pr-1.5 py-1">
                <Eye className="h-3.5 w-3.5 text-[#BA0C2F]" />
                <select
                  data-testid="view-as-select"
                  value={viewAs || ""}
                  onChange={(e) => setViewAs(e.target.value || null)}
                  className="bg-transparent text-xs font-semibold text-[#8A0822] outline-none cursor-pointer"
                >
                  <option value="">View as: Admin</option>
                  <option value="pmo">PMO</option>
                  <option value="executive">Executive</option>
                  <option value="it_executive">IT Executive</option>
                  <option value="engineer">Engineer</option>
                </select>
              </div>
            )}

            <div className="relative">
              <button data-testid="notifications-btn" onClick={() => setShowNotif((v) => !v)}
                className="relative h-9 w-9 rounded-full hover:bg-[#F4F6FA] flex items-center justify-center transition-colors">
                <Bell className="h-5 w-5 text-[#5A687C]" />
                {unread > 0 && <span className="absolute top-1 right-1 h-4 w-4 rounded-full bg-[#BA0C2F] text-white text-[10px] font-bold flex items-center justify-center">{unread}</span>}
              </button>
              {showNotif && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl border border-[#DCE2EA] shadow-xl overflow-hidden z-50" data-testid="notifications-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-[#DCE2EA]">
                    <span className="text-sm font-bold text-[#1F2733]">Notifications</span>
                    <button onClick={markAllRead} className="text-xs font-semibold text-[#BA0C2F] hover:underline">Mark all read</button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 && <div className="px-4 py-6 text-sm text-[#5A687C] text-center">No notifications</div>}
                    {notifs.map((n) => (
                      <button key={n.id} onClick={() => { if (n.link) nav(n.link); api.post(`/notifications/${n.id}/read`).then(loadNotifs); setShowNotif(false); }}
                        className={`w-full text-left px-4 py-3 hover:bg-[#F4F6FA] border-b border-[#DCE2EA]/50 ${n.read ? "opacity-60" : ""}`}>
                        <div className="text-sm text-[#1F2733]">{n.message}</div>
                        <div className="text-[11px] text-[#98A2AF] mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
                      </button>
                    ))}
                  </div>
                  <button data-testid="view-all-notifications" onClick={() => { nav("/notifications"); setShowNotif(false); }} className="w-full text-center py-2.5 text-xs font-semibold text-[#BA0C2F] hover:bg-[#F4F6FA] border-t border-[#DCE2EA]">View all notifications</button>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 pl-2 border-l border-[#DCE2EA]">
              <div className="h-9 w-9 rounded-full bg-[#BA0C2F] text-white flex items-center justify-center font-bold text-sm">
                {(user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </div>
              <div className="hidden xl:block leading-tight">
                <div className="text-xs font-bold text-[#1F2733]">{user?.name}</div>
                <div className="text-[11px] text-[#5A687C]">{ROLE_LABELS[user?.role]}</div>
              </div>
              <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }}
                className="h-9 w-9 rounded-full hover:bg-[#FDF2F4] flex items-center justify-center transition-colors group">
                <LogOut className="h-4.5 w-4.5 text-[#5A687C] group-hover:text-[#BA0C2F]" />
              </button>
            </div>
          </div>
        </div>

        <nav className="max-w-[1600px] mx-auto px-4 lg:px-8 pb-3 -mt-1">
          <div className="flex items-center gap-1 overflow-x-auto bg-[#F4F6FA] p-1.5 rounded-full border border-[#DCE2EA] w-fit max-w-full">
            {items.map((n) => {
              const active = loc.pathname === n.to || (n.to !== "/" && loc.pathname.startsWith(n.to));
              const Icon = n.icon;
              return (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all ${
                    active ? "bg-[#BA0C2F] text-white shadow-md font-semibold" : "text-[#5A687C] hover:text-[#1F2733] hover:bg-white/70 font-medium"}`}>
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{n.label}</span>
                </Link>
              );
            })}
            <a href="/portal-overview.html" target="_blank" rel="noopener noreferrer" data-testid="nav-program-overview"
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all text-[#5A687C] hover:text-[#1F2733] hover:bg-white/70 font-medium">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Program Overview</span>
            </a>
          </div>
        </nav>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6 lg:py-8 animate-fade-up">{children}</main>
    </div>
  );
}
